import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Home, Star, HandMetal, MessageCircle, Menu, LogIn, Sparkles, CreditCard, Heart } from 'lucide-react';
import './Header.css';

const NavMenuContent: React.FC<{
  isAuthenticated: boolean;
  user: any;
  closeMenu: () => void;
  handleLogout: () => void;
  handleNavClick: (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => void;
}> = ({ isAuthenticated, user, closeMenu, handleLogout, handleNavClick }) => (
  <div className="nav-right">
    <ul className="nav-links">
      {!isAuthenticated ? (
        <>
          <li>
            <a 
              href="/#features" 
              className="nav-link"
              onClick={(e) => handleNavClick(e, 'features')}
            >
              Features
            </a>
          </li>
          <li>
            <a 
              href="/#how" 
              className="nav-link"
              onClick={(e) => handleNavClick(e, 'how')}
            >
              About
            </a>
          </li>
          <li>
            <NavLink to="/login" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={closeMenu}>
              Sign In
            </NavLink>
          </li>
          <li>
            <NavLink to="/signup" className={({ isActive }) => isActive ? 'nav-link active btn-premium' : 'nav-link btn-premium'} onClick={closeMenu}>
              Sign Up
            </NavLink>
          </li>
        </>
      ) : (
        <>
          <li>
            <NavLink to="/horoscope" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={closeMenu}>
              Horoscope
            </NavLink>
          </li>
          <li>
            <NavLink to="/palmistry" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={closeMenu}>
              Palmistry
            </NavLink>
          </li>
          <li>
            <NavLink to="/compatibility" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={closeMenu}>
              Compatibility
            </NavLink>
          </li>
          <li>
            <NavLink to="/chat" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={closeMenu}>
              Chat
            </NavLink>
          </li>
          <li>
            <NavLink to="/profile" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={closeMenu}>
              Profile
            </NavLink>
          </li>
          <li>
            <span className="nav-link user-name">{user?.name}</span>
          </li>
          <li>
            <button onClick={handleLogout} className="nav-link logout-btn">
              Logout
            </button>
          </li>
        </>
      )}
      <li>
        <NavLink to="/plans" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={closeMenu}>
          Plans
        </NavLink>
      </li>
    </ul>
  </div>
);

const BottomNavBar: React.FC<{
  isAuthenticated: boolean;
  handleNavClick: (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => void;
  toggleMenu: () => void;
}> = ({ isAuthenticated, handleNavClick, toggleMenu }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  
  return (
    <div className="bottom-nav-bar">
      {!isAuthenticated ? (
        <>
          <NavLink to="/" className={({isActive}) => isActive ? "bottom-nav-item active" : "bottom-nav-item"} end>
            <Home size={20} />
            <span>Home</span>
          </NavLink>
          <a href="/#features" className={`bottom-nav-item ${isHome && location.hash === '#features' ? 'active' : ''}`} onClick={(e) => handleNavClick(e, 'features')}>
            <Sparkles size={20} />
            <span>Features</span>
          </a>
          <NavLink to="/login" className={({isActive}) => isActive ? "bottom-nav-item active" : "bottom-nav-item"}>
            <LogIn size={20} />
            <span>Login</span>
          </NavLink>
        </>
      ) : (
        <>
          <NavLink to="/horoscope" className={({isActive}) => isActive ? "bottom-nav-item active" : "bottom-nav-item"}>
            <Star size={20} />
            <span>Horoscope</span>
          </NavLink>
          <NavLink to="/palmistry" className={({isActive}) => isActive ? "bottom-nav-item active" : "bottom-nav-item"}>
            <HandMetal size={20} />
            <span>Palm</span>
          </NavLink>
          <NavLink to="/chat" className={({isActive}) => isActive ? "bottom-nav-item active" : "bottom-nav-item"}>
            <MessageSquare size={20} />
            <span>Chat</span>
          </NavLink>
          {isAuthenticated && (
            <NavLink to="/profile" className={({isActive}) => isActive ? "bottom-nav-item active" : "bottom-nav-item"}>
              <User size={20} />
              <span>Profile</span>
            </NavLink>
          )}
        </>
      )}
    </div>
  );
};

const Header: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);



  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    navigate('/');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    closeMenu();
    if (window.location.pathname === '/') {
      e.preventDefault();
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Inline styles for the mobile drawer - applied directly to bypass WebView bugs.
  // Uses top:0 + bottom:0 instead of height:100vh (broken in Android WebView).
  const mobileDrawerStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    bottom: 0,
    right: isMenuOpen ? 0 : '-100%',
    width: '280px',
    backgroundColor: '#0b1e3a', // Solid, opaque midnight blue to fix transparent background
    borderLeft: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    padding: '80px 0 20px',
    transition: 'right 0.3s ease',
    boxShadow: isMenuOpen ? '-5px 0 20px rgba(0,0,0,0.7)' : 'none',
    zIndex: 10001,
    overflowY: 'auto',
  };

  const backdropStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 10000,
    opacity: isMenuOpen ? 1 : 0,
    pointerEvents: isMenuOpen ? 'auto' : 'none',
    transition: 'opacity 0.3s ease',
  };

  // On mobile, render the drawer via a portal directly on document.body
  // so it is completely outside the sticky header's stacking context
  const mobileDrawer = isMobile
    ? ReactDOM.createPortal(
        <>
          <div style={backdropStyle} onClick={closeMenu} />
          <div style={mobileDrawerStyle} className={`nav-menu-portal ${isMenuOpen ? 'active' : ''}`}>
            <NavMenuContent
              isAuthenticated={isAuthenticated}
              user={user}
              closeMenu={closeMenu}
              handleLogout={handleLogout}
              handleNavClick={handleNavClick}
            />
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <>
      <header className="header">
        <nav className="navbar">
          <div className="container">
            <div className="navbar-content">
              <NavLink to="/" className="logo" onClick={closeMenu}>
                <img src="/favicon.png" alt="AstroML Logo" className="logo-icon" />
                <div className="logo-text-wrapper">
                  <span className="logo-text">AstroML</span>
                  <span className="logo-subtitle">Nakshatra</span>
                </div>
              </NavLink>
              
              <button className={`menu-toggle ${isMenuOpen ? 'open' : ''}`} onClick={toggleMenu} aria-label="Toggle menu">
                <span></span>
                <span></span>
                <span></span>
              </button>

              {/* Desktop: render nav-menu inline as before */}
              {!isMobile && (
                <div className="nav-menu">
                  <NavMenuContent
                    isAuthenticated={isAuthenticated}
                    user={user}
                    closeMenu={closeMenu}
                    handleLogout={handleLogout}
                    handleNavClick={handleNavClick}
                  />
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>
      {/* Mobile: render drawer outside header via portal */}
      {mobileDrawer}
      {/* Mobile: render Bottom Navigation Bar */}
      {isMobile && (
        <BottomNavBar 
          isAuthenticated={isAuthenticated} 
          handleNavClick={handleNavClick} 
          toggleMenu={toggleMenu} 
        />
      )}
    </>
  );
};


export default Header;
